import { withDeviceHeader } from "./device";

type TokenListener = (token: string | null) => void;

const listeners = new Set<TokenListener>();
let initialized = false;
let currentToken: string | null = null;
let refreshInFlight: Promise<string | null> | null = null;
const AUTH_LOST_EVENT = "echo:auth-lost";
const API_URL =
  import.meta.env.VITE_API_URL ??
  import.meta.env.VITE_API_HTTP_URL ??
  "/api";

declare global {
  interface Window {
    __ECHO_AUTH_TOKEN__?: string | null;
  }
}

function readToken(): string | null {
  try {
    if (localStorage.getItem("__echo_logged_out__") === "1") return null;
  } catch {}
  try {
    const runtimeToken = window.__ECHO_AUTH_TOKEN__;
    if (typeof runtimeToken === "string" && runtimeToken) return runtimeToken;
  } catch {}
  try {
    const restored = localStorage.getItem("token");
    if (restored) return restored;
  } catch {}
  return currentToken;
}

export function syncAuthTokenFromRuntime(): string | null {
  const token = readToken();
  if (token && token !== currentToken) {
    currentToken = token;
    try {
      window.__ECHO_AUTH_TOKEN__ = token;
    } catch {}
    for (const listener of listeners) {
      try {
        listener(token);
      } catch {}
    }
  }
  return token;
}

export function getAuthToken(): string | null {
  if (!initialized) {
    currentToken = readToken();
    initialized = true;
  }
  return currentToken;
}

export async function refreshAuthToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${API_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: withDeviceHeader(),
      });
      if (!res.ok) return null;
      const data = await res.json().catch(() => ({}));
      const token = (data as any)?.token;
      if (typeof token === "string" && token) {
        setAuthToken(token);
        return token;
      }
      return null;
    } catch {
      return null;
    }
  })();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

export async function hydrateAuthTokenFromNative(): Promise<string | null> {
  return (await refreshAuthToken()) ?? currentToken;
}

export function setAuthToken(token: string | null) {
  if (!initialized) {
    currentToken = readToken();
    initialized = true;
  }
  const next = token ?? null;
  currentToken = next;
  try {
    if (next) {
      localStorage.setItem("token", next);
      localStorage.removeItem("__echo_logged_out__");
    } else {
      localStorage.removeItem("token");
      localStorage.setItem("__echo_logged_out__", "1");
    }
  } catch {}
  try {
    window.__ECHO_AUTH_TOKEN__ = next;
  } catch {}
  for (const listener of listeners) {
    try {
      listener(next);
    } catch {}
  }
}

export function subscribeAuthToken(listener: TokenListener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function notifyAuthLost() {
  try {
    setAuthToken(null);
    window.dispatchEvent(new CustomEvent(AUTH_LOST_EVENT));
  } catch {
    setAuthToken(null);
  }
}

export function onAuthLost(handler: () => void) {
  try {
    window.addEventListener(AUTH_LOST_EVENT, handler);
    return () => window.removeEventListener(AUTH_LOST_EVENT, handler);
  } catch {
    return () => {};
  }
}
